/**
 * @file: This file lights the recipe lines that a command just added.
 *
 * A label chip adds candidates at count 0. A long recipe puts those lines under
 * the edge of the list, and then the colour of the chip is the only sign that
 * the click did anything. This manager marks the new lines for a moment and
 * brings the group of the first one to the top of the list. The head of the
 * group names the kind, so the operator sees where the new lines came in.
 *
 * The manager reads the view model of the controller, because the lines of the
 * recipe are there. It writes the mark on each line, so the template reads a
 * property and calls no function on every digest.
 *
 * A line that the search or Hide unused takes out of the list cannot be lit.
 * The mark is on the lines the page shows, and a hidden line has none. The
 * how-to of the page says that.
 */

/** The class of the scroll box of a list. Both lists of the page write it. */
const SCROLL_BOX = '.jumbotron-scroll'

/** The attribute that names the media of a recipe line. */
const LINE_KEY = 'data-line-key'

/** The class of the head of a group. It is the first child of the group. */
const GROUP_HEAD = '.playlist-compose-group'

/**
 * How long a new line stays lit. The operator needs time to find the line, and
 * a mark that stays becomes part of the list.
 */
const FRESH_MS = 2500

class FreshLines {
  constructor (controller) {
    this.controller = controller
    this._keys = new Set()
    this._before = new Set()
    this._clearTimer = null
    this._scrollTimer = null
  }

  /** Give up the timers. Called when the page goes away. */
  detach () {
    const $timeout = this.controller._$timeout

    $timeout.cancel(this._clearTimer)
    $timeout.cancel(this._scrollTimer)
    this._clearTimer = null
    this._scrollTimer = null
  }

  /** Remember the lines the recipe shows now. Call this before a command. */
  noteBefore () {
    this._before = new Set(this._lineKeys())
  }

  /**
   * Light the lines that arrived since noteBefore, and bring the first one into
   * view. Call this after the command and after the view model is built again.
   */
  markAfter () {
    const fresh = this._lineKeys().filter(key => !this._before.has(key))

    if (fresh.length === 0) {
      return
    }

    this._keys = new Set(fresh)
    this.stamp()
    this._clearLater()
    this._scrollTo(fresh[0])
  }

  /** True while the line is one that a command just added. */
  has (key) {
    return this._keys.has(key)
  }

  /**
   * Write the mark on every line of the view model. The controller calls this
   * each time it builds the view model, so a line keeps its mark while another
   * command runs.
   */
  stamp () {
    this._groups().forEach(group => group.lines.forEach(line => {
      line.fresh = this._keys.has(line.key)
    }))
  }

  // ===== The parts of the two commands =====

  _groups () {
    const view = this.controller.view

    return (view && view.groups) || []
  }

  /** The keys of the lines the recipe shows, in the order of the page. */
  _lineKeys () {
    return this._groups().flatMap(group => group.lines.map(line => line.key))
  }

  /** Take the mark off again. The timer of Angular also redraws the page. */
  _clearLater () {
    const $timeout = this.controller._$timeout

    $timeout.cancel(this._clearTimer)
    this._clearTimer = $timeout(() => {
      this._keys = new Set()
      this.stamp()
      this._clearTimer = null
    }, FRESH_MS)
  }

  /**
   * Put the head of the group of the first new line at the top of its list.
   * The new lines are under the head, after the lines that are in the list.
   * The line alone at the top does not show which group it belongs to.
   *
   * The rows are drawn by the digest that follows the command, so the scroll
   * waits for a timer of zero. Only the list scrolls, and not the page, so the
   * chips and the toolbar stay where the operator left them.
   */
  _scrollTo (key) {
    const $timeout = this.controller._$timeout

    $timeout.cancel(this._scrollTimer)
    this._scrollTimer = $timeout(() => {
      this._scrollTimer = null

      const row = this.controller._$element[0].querySelector(`[${LINE_KEY}="${key}"]`)
      const box = row && row.closest(SCROLL_BOX)

      if (!box) {
        return
      }

      const target = this._groupHeadOf(row) || row

      // The two rectangles give the distance without a rule about which element
      // the offsets are measured from.
      box.scrollTop += target.getBoundingClientRect().top - box.getBoundingClientRect().top
    })
  }

  /** The head of the group that the row is in, or null when the row has none. */
  _groupHeadOf (row) {
    const group = row.parentElement

    return group ? group.querySelector(`:scope > ${GROUP_HEAD}`) : null
  }
}

export { FreshLines, FRESH_MS }
